import React, { useEffect, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { ReactComponent as CheckIcon } from '../assets/check.svg';
import { ReactComponent as WarningIcon } from '../assets/warning.svg';
import './Button.scss';

export type ButtonType =
  | 'Primary'
  | 'Secondary'
  | 'Tertiary'
  | 'Outline'
  | 'White'
  | 'White Outline'
  | 'Link';

export type ButtonSize = 'Default' | 'Small';
export type ActionResult = 'Success' | 'Error' | null;

type ButtonProps = {
  isLoading?: boolean;
  isSubmit?: boolean;
  className?: string;
  onClick?:
    | (() => void)
    | (() => ActionResult)
    | (() => Promise<void>)
    | (() => Promise<ActionResult>)
    | undefined;
  children: React.ReactNode;
  disabled?: boolean;
  type?: ButtonType;
  size?: ButtonSize;
  loadingText?: string;
  successText?: string;
  isIcon?: boolean;
  title?: string;
  id?: string;
  width?: number;
  loadingTextAfter?: boolean;
  'aria-label'?: string;
};

const Button: React.FC<ButtonProps> = ({
  isLoading,
  isSubmit,
  onClick,
  children,
  disabled,
  className,
  type,
  size,
  loadingText,
  successText,
  isIcon,
  title,
  id,
  width,
  loadingTextAfter,
  'aria-label': ariaLabel
}) => {
  const [result, setResult] = useState<ActionResult>(null);

  useEffect(() => {
    if (!result) {
      return;
    }

    const timeout = setTimeout(() => {
      setResult(null);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [result]);

  const typeClassMap: Record<ButtonType, string> = {
    Primary: 'button-primary',
    Secondary: 'button-secondary',
    Tertiary: 'button-tertiary',
    Outline: 'button-outline',
    White: 'button-white',
    'White Outline': 'button-white-outline',
    Link: 'button-link'
  };
  const typeSizeMap: Record<ButtonSize, string> = {
    Default: 'button-default',
    Small: 'button-small'
  };

  const typeClass = typeClassMap[type ?? 'Primary'];
  const typeSize = typeSizeMap[size ?? 'Default'];

  const contents = () => {
    if (isLoading) {
      return (
        <>
          {!loadingTextAfter && loadingText}
          <FaSpinner className="spinner" />
          {loadingTextAfter && loadingText}
        </>
      );
    }

    if (result === 'Success') {
      return (
        <>
          {successText ?? 'Saved'}
          <CheckIcon />
        </>
      );
    }
    if (result === 'Error') {
      return (
        <>
          Error
          <WarningIcon />
        </>
      );
    }

    return children;
  };

  const feedbackClass = () => {
    const classMap: Record<Exclude<ActionResult, null>, string> = {
      Success: 'app-button__success',
      Error: 'app-button__error'
    };

    return result ? (classMap[result] ?? '') : '';
  };

  return (
    <button
      id={id}
      title={title}
      type={isSubmit ? 'submit' : 'button'}
      className={`app-button ${className ?? ''} ${typeClass} ${typeSize} ${isLoading ? 'button-loading' : ''} ${isIcon ? 'icon-button' : ''} ${feedbackClass()}`}
      onClick={async () => {
        if (!onClick) {
          return;
        }
        try {
          const result = await onClick();
          setResult(result ?? null);
        } catch (error) {
          setResult('Error');
        }
      }}
      disabled={isLoading || disabled}
      style={{ width: width }}
      aria-label={ariaLabel}
    >
      <span className="button-content">{contents()}</span>
    </button>
  );
};

export default Button;
